import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  authError: string | null;
  isRefreshPaused: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  retryConnection: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cooldown configuration
const REFRESH_COOLDOWN_MS = 60000; // 1 minute cooldown after failures
const MAX_REFRESH_FAILURES = 3;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isRefreshPaused, setIsRefreshPaused] = useState(false);
  
  const refreshFailureCount = useRef(0);
  const lastRefreshAttempt = useRef<number>(0);
  const cooldownTimeout = useRef<NodeJS.Timeout | null>(null);

  const clearCooldown = useCallback(() => {
    if (cooldownTimeout.current) {
      clearTimeout(cooldownTimeout.current);
      cooldownTimeout.current = null;
    }
    refreshFailureCount.current = 0;
    setIsRefreshPaused(false);
    setAuthError(null);
  }, []);

  // Helper to classify errors
  const classifyError = (err: any): string => {
    const message = err?.message || '';
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return "Network unreachable. Check your internet or try disabling browser extensions.";
    }
    if (message.includes('timeout') || message.includes('AbortError') || err?.name === 'AbortError') {
      return "Request timed out. The server may be slow or unreachable.";
    }
    if (message.includes('Invalid login credentials')) {
      return "Invalid email or password.";
    }
    if (message.includes('Email not confirmed')) {
      return "Please confirm your email before signing in.";
    }
    return message || "Connection failed. Please try again.";
  };

  // Promise-based timeout wrapper
  const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms)
      ),
    ]);
  };

  const retryConnection = useCallback(async () => {
    clearCooldown();
    setAuthError(null);
    
    try {
      const { data: { session }, error } = await withTimeout(
        supabase.auth.getSession(),
        10000 // 10s timeout
      );
      
      if (error) {
        console.error("Retry connection failed:", error);
        setAuthError(classifyError(error));
      } else {
        setSession(session);
        setUser(session?.user ?? null);
        if (session) {
          setAuthError(null);
        }
      }
    } catch (err: any) {
      console.error("Retry connection error:", err);
      setAuthError(classifyError(err));
    }
  }, [clearCooldown]);

  useEffect(() => {
    // Set up auth state listener with error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state change:", event);
        
        if (event === 'TOKEN_REFRESHED') {
          // Reset failure count on successful refresh
          refreshFailureCount.current = 0;
          setIsRefreshPaused(false);
          setAuthError(null);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Initial session check with error handling and Promise.race timeout
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await withTimeout(
          supabase.auth.getSession(),
          15000 // 15s timeout
        );
        
        if (error) {
          console.error("Initial session check failed:", error);
          
          // Check if it's a network error
          const errorMsg = error.message || '';
          if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
            refreshFailureCount.current++;
            lastRefreshAttempt.current = Date.now();
            
            if (refreshFailureCount.current >= MAX_REFRESH_FAILURES) {
              setIsRefreshPaused(true);
              setAuthError(classifyError(error));
              
              // Auto-retry after cooldown
              cooldownTimeout.current = setTimeout(() => {
                setIsRefreshPaused(false);
                refreshFailureCount.current = 0;
              }, REFRESH_COOLDOWN_MS);
            }
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
      } catch (err: any) {
        console.error("Session check failed:", err);
        setAuthError(classifyError(err));
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();

    // Listen for visibility changes to retry when user comes back
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRefreshPaused) {
        // Auto-retry when user comes back and we're paused
        const timeSinceLastAttempt = Date.now() - lastRefreshAttempt.current;
        if (timeSinceLastAttempt > REFRESH_COOLDOWN_MS / 2) {
          retryConnection();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (cooldownTimeout.current) {
        clearTimeout(cooldownTimeout.current);
      }
    };
  }, [isRefreshPaused, retryConnection]);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        15000 // 15s timeout
      );
      
      if (error) {
        setAuthError(classifyError(error));
      } else {
        // Clear any auth errors on successful sign in
        clearCooldown();
      }
      
      return { error };
    } catch (err: any) {
      const errorMsg = classifyError(err);
      setAuthError(errorMsg);
      return { error: new Error(errorMsg) };
    }
  };

  const signOut = async () => {
    clearCooldown();
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { error };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      authError,
      isRefreshPaused,
      signUp, 
      signIn, 
      signOut, 
      resetPassword, 
      updatePassword,
      retryConnection
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
